/** Pairing / device page. */
import { useEffect, useMemo, useState } from 'react';
import { fingerprint } from '../lib/crypto/e2e';
import { formatClock, shortId } from '../lib/format';
import { normalizeDaemonDeviceId, secureCryptoStatus } from '../lib/live/plan';
import { normalizeWorkerUrl } from '../lib/settings';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { QrScanner, isQrScanSupported } from '../components/QrScanner';
import { StatusBanner } from '../components/StatusBanner';
import type { Route } from '../components/NavBar';

function normalizeCodeInput(raw: string): string {
  const compact = raw
    .replace(/[\s-]/gu, '')
    .toUpperCase()
    .replace(/[^A-Z2-9]/gu, '')
    .slice(0, 8);
  return compact.length > 4 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact;
}

function isCompleteCode(code: string): boolean {
  return /^[A-Z2-9]{4}-[A-Z2-9]{4}$/u.test(code);
}

function initialCodeFromUrl(): string {
  try {
    const url = new URL(window.location.href);
    const direct = url.searchParams.get('code');
    if (direct) return normalizeCodeInput(direct);
    const hashQuery = /[?&]code=([^&]+)/u.exec(url.hash);
    if (hashQuery?.[1]) return normalizeCodeInput(decodeURIComponent(hashQuery[1]));
  } catch {
    /* ignore */
  }
  return '';
}

export function PairingPage({
  onPaired,
  onNavigate,
}: {
  onPaired(): void;
  onNavigate(route: Route): void;
}) {
  const {
    paired,
    identity,
    settings,
    updateSettings,
    pair,
    unpair,
    testWorkerConnection,
    relayPairing,
    startRelayPairing,
    retryRelayPairing,
    cancelRelayPairing,
  } = useApp();
  const [code, setCode] = useState(initialCodeFromUrl);
  const [name, setName] = useState(settings.displayName);
  const [workerUrl, setWorkerUrl] = useState(settings.workerUrl);
  const [daemonIdInput, setDaemonIdInput] = useState(settings.daemonDeviceId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workerCheck, setWorkerCheck] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [confirmUnpair, setConfirmUnpair] = useState(false);
  const secure = useMemo(() => secureCryptoStatus(), []);

  const canSubmit = isCompleteCode(code) && name.trim().length > 0 && !busy;
  const scanningSupported = useMemo(() => isQrScanSupported(), []);

  useEffect(() => {
    setName(settings.displayName);
    setWorkerUrl(settings.workerUrl);
    setDaemonIdInput(settings.daemonDeviceId);
  }, [settings.displayName, settings.workerUrl, settings.daemonDeviceId]);

  const saveWorkerUrl = (): void => {
    updateSettings({ workerUrl });
    setWorkerCheck(
      normalizeWorkerUrl(workerUrl) ? 'Worker 地址已保存' : 'Worker 地址无效（需 http(s)://）',
    );
  };

  const submit = async (overrideCode?: string): Promise<void> => {
    const targetCode = overrideCode ?? code;
    if (!isCompleteCode(targetCode) || busy) return;
    setBusy(true);
    setError(null);
    const result = await pair(targetCode, name.trim() || settings.displayName);
    setBusy(false);
    if (result.ok) {
      onPaired();
    } else {
      setError(result.error ?? '配对失败');
    }
  };

  const runWorkerCheck = async (): Promise<void> => {
    setWorkerCheck('正在连接…');
    const result = await testWorkerConnection();
    setWorkerCheck(result.message);
  };

  if (paired) {
    return (
      <div className="h-full overflow-y-auto">
        <StatusBanner />
        <div className="space-y-4 px-4 pb-6">
          <section className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-4">
            <div className="flex items-center gap-2 text-emerald-200">
              <Icon name="check" className="h-5 w-5" />
              <h2 className="text-sm font-semibold">设备已配对</h2>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              {settings.demoMode
                ? '演示模式（mock daemon），不连接真实后端。'
                : '已连接 Worker 中继。'}
            </p>
            <dl className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">设备名</dt>
                <dd className="text-slate-200">{identity?.name ?? settings.displayName}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">设备 ID</dt>
                <dd className="font-mono text-slate-300">
                  {shortId(identity?.device_id ?? paired.device_id, 22)}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">配对时间</dt>
                <dd className="text-slate-300">{formatClock(paired.paired_at)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">令牌到期</dt>
                <dd className="text-slate-300">{formatClock(paired.expires_at)}</dd>
              </div>
              {identity ? (
                <>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">E2E 公钥指纹</dt>
                    <dd className="font-mono text-slate-300">
                      {fingerprint(identity.e2e_public_key, 3)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-slate-500">签名公钥指纹</dt>
                    <dd className="font-mono text-slate-300">
                      {fingerprint(identity.signing_public_key, 3)}
                    </dd>
                  </div>
                </>
              ) : null}
            </dl>
          </section>

          <section className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onNavigate('chat')}
              className="rounded-xl bg-indigo-500 py-3 text-sm font-medium text-white"
            >
              进入聊天
            </button>
            <button
              type="button"
              onClick={() => onNavigate('approvals')}
              className="rounded-xl border border-white/10 bg-white/5 py-3 text-sm text-slate-200"
            >
              审批中心
            </button>
          </section>

          <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-4 text-xs text-slate-400">
            <h3 className="text-sm font-medium text-slate-200">配对关系</h3>
            <p className="mt-1">
              手机持有 X25519（E2E）与 Ed25519（审批签名）私钥；私钥只保存在本机浏览器存储中，不上传
              Worker。
            </p>
            <p className="mt-1">吊销/解绑会清空本地令牌与审批缓存；可随时重新配对。</p>
            <button
              type="button"
              onClick={() => setConfirmUnpair(true)}
              className="mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-1.5 text-rose-200"
            >
              解除配对
            </button>
          </section>

          <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-4 text-xs text-slate-400">
            <h3 className="text-sm font-medium text-slate-200">连接 PC daemon（中继引导配对）</h3>
            {settings.daemonE2ePublicKey && settings.daemonDeviceId ? (
              <div className="mt-2 space-y-1.5">
                <p className="text-emerald-200">
                  daemon 公钥已保存，live relay 配置就绪（审批/聊天/执行经 E2E 中继）。
                </p>
                <p className="flex justify-between gap-3">
                  <span>daemon device id</span>
                  <span className="font-mono text-slate-300">{settings.daemonDeviceId}</span>
                </p>
                <p className="flex justify-between gap-3">
                  <span>daemon X25519 公钥</span>
                  <span className="font-mono text-slate-300">
                    {fingerprint(settings.daemonE2ePublicKey, 3)}
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => startRelayPairing(settings.daemonDeviceId)}
                  className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
                >
                  重新发起引导配对
                </button>
              </div>
            ) : settings.demoMode ? (
              <p className="mt-1">
                演示模式无需连接真实 daemon；关闭演示模式并完成 Worker
                配对后，在此进行一次性中继引导配对。
              </p>
            ) : (
              <div className="mt-1 space-y-3">
                <p className="leading-relaxed">
                  Worker 配对只发放设备 token；手机与 daemon 的 E2E 通道还需要交换 X25519
                  公钥。引导请求首帧只含公钥/设备名/时间（**不含 token 或私钥**），由你在 PC
                  上确认后 daemon 回传公钥。
                </p>
                {!secure.ok ? (
                  <p className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200">
                    {secure.reason}。HTTP 局域网下无法建立 E2E 通道；本引导请求本身为明文公钥，
                    但之后的 live 审批/执行需要 HTTPS（可用 localhost 或 HTTPS 隧道）。
                  </p>
                ) : null}
                <label className="flex flex-col gap-1 text-xs text-slate-300">
                  <span>daemon device id（dev_…）</span>
                  <input
                    value={daemonIdInput}
                    onChange={(event) => setDaemonIdInput(event.target.value)}
                    placeholder="dev_xxxxxxxxxxxx"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 font-mono text-sm outline-none focus:border-indigo-400/60"
                  />
                </label>
                <p className="text-[10px] leading-relaxed text-slate-500">
                  在 PC 上运行以下命令获取 daemon device id 与 X25519 公钥（仅公开值）：
                  <code className="mt-1 block break-all rounded bg-black/30 px-2 py-1 font-mono text-[10px] text-indigo-200">
                    node --import tsx apps/server/scripts/approve-relay-pairing.ts --identity
                  </code>
                </p>
                <button
                  type="button"
                  disabled={
                    !normalizeDaemonDeviceId(daemonIdInput) ||
                    relayPairing.status === 'connecting' ||
                    relayPairing.status === 'waiting' ||
                    relayPairing.status === 'accepted'
                  }
                  onClick={() => startRelayPairing(daemonIdInput)}
                  className="w-full rounded-xl bg-indigo-500 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                >
                  发送引导配对请求
                </button>
              </div>
            )}

            {relayPairing.status === 'connecting' ||
            relayPairing.status === 'waiting' ||
            relayPairing.status === 'disconnected' ? (
              <div className="mt-3 rounded-xl border border-indigo-400/25 bg-indigo-400/5 p-3 text-[11px] leading-relaxed text-indigo-100">
                <p>{relayPairing.message ?? '等待 PC 确认…'}</p>
                {relayPairing.request_id ? (
                  <>
                    <p className="mt-2 text-[10px] text-indigo-200/70">request_id</p>
                    <code className="mt-1 block break-all rounded bg-black/30 px-2 py-1 font-mono text-[10px] text-indigo-200">
                      {relayPairing.request_id}
                    </code>
                    <p className="mt-2 text-[10px] text-indigo-200/70">在 PC 上运行：</p>
                    <code className="mt-1 block break-all rounded bg-black/30 px-2 py-1 font-mono text-[10px] text-indigo-200">
                      node --import tsx apps/server/scripts/approve-relay-pairing.ts --approve{' '}
                      {relayPairing.request_id}
                    </code>
                  </>
                ) : null}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={retryRelayPairing}
                    className="rounded-lg border border-indigo-400/40 bg-indigo-400/10 px-3 py-1.5 text-xs text-indigo-100"
                  >
                    重发请求
                  </button>
                  <button
                    type="button"
                    onClick={cancelRelayPairing}
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300"
                  >
                    取消
                  </button>
                </div>
              </div>
            ) : null}

            {relayPairing.status === 'accepted' ? (
              <div className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-400/5 p-3 text-[11px] leading-relaxed text-emerald-200">
                <p>{relayPairing.message ?? 'PC 已确认配对'}</p>
                {relayPairing.daemon_device_id ? (
                  <p className="mt-1 font-mono text-[10px]">{relayPairing.daemon_device_id}</p>
                ) : null}
              </div>
            ) : null}

            {relayPairing.status === 'denied' ||
            relayPairing.status === 'timeout' ||
            relayPairing.status === 'error' ? (
              <div className="mt-3 rounded-xl border border-rose-400/25 bg-rose-400/5 p-3 text-[11px] leading-relaxed text-rose-200">
                <p>{relayPairing.error ?? relayPairing.message ?? '引导配对失败'}</p>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={retryRelayPairing}
                    disabled={!relayPairing.retryable}
                    className="rounded-lg border border-rose-400/40 bg-rose-400/10 px-3 py-1.5 text-xs text-rose-100 disabled:opacity-40"
                  >
                    重试
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
        <ConfirmDialog
          open={confirmUnpair}
          title="解除配对？"
          danger
          confirmLabel="解除配对"
          onCancel={() => setConfirmUnpair(false)}
          onConfirm={() => {
            unpair();
            setConfirmUnpair(false);
          }}
        >
          <p>将清除本机设备令牌与审批缓存（PIN 与聊天记录保留，可在设置中清理）。</p>
        </ConfirmDialog>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <StatusBanner />
      <div className="space-y-4 px-4 pb-6">
        <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/15 to-transparent p-4">
          <h2 className="text-base font-semibold text-slate-100">配对你的手机</h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-400">
            PC 端会生成含 Worker 地址和一次性 code 的二维码（M0.1 也可手动输入 code）。配对时交换
            X25519 公钥并签发设备 token；token 只存哈希。
          </p>
        </section>

        <section className="space-y-3 rounded-2xl border border-white/10 bg-[#0f1629] p-4">
          <label className="flex flex-col gap-1.5 text-xs text-slate-300">
            <span>配对 code（一次性，10 分钟内有效）</span>
            <div className="flex gap-2">
              <input
                value={code}
                onChange={(event) => setCode(normalizeCodeInput(event.target.value))}
                placeholder="ABCD-2345"
                inputMode="text"
                autoCapitalize="characters"
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 font-mono text-base tracking-[0.2em] outline-none focus:border-indigo-400/60"
              />
              {scanningSupported ? (
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="flex items-center gap-1 rounded-xl border border-white/10 bg-white/5 px-3 text-xs text-slate-200"
                >
                  <Icon name="scan" className="h-4 w-4" />
                  扫码
                </button>
              ) : null}
            </div>
          </label>

          <label className="flex flex-col gap-1.5 text-xs text-slate-300">
            <span>设备名</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 40))}
              className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm outline-none focus:border-indigo-400/60"
              placeholder="我的手机"
            />
          </label>

          <label className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
            <span className="flex flex-col">
              <span className="text-sm text-slate-200">演示模式（Mock daemon）</span>
              <span className="text-[11px] text-slate-500">
                不连接真实 PC/Worker，用于 UI 与流程验收
              </span>
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 accent-indigo-500"
              checked={settings.demoMode}
              onChange={(event) => updateSettings({ demoMode: event.target.checked })}
            />
          </label>

          {!settings.demoMode ? (
            <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-black/20 p-3">
              <span className="text-xs text-slate-300">Worker 地址</span>
              <input
                value={workerUrl}
                onChange={(event) => setWorkerUrl(event.target.value)}
                placeholder="https://wintersrain.workers.dev"
                inputMode="url"
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs outline-none focus:border-indigo-400/60"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveWorkerUrl}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
                >
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => void runWorkerCheck()}
                  className="rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-3 py-1.5 text-xs text-indigo-200"
                >
                  测试连接
                </button>
              </div>
              {workerCheck ? <p className="text-[11px] text-slate-400">{workerCheck}</p> : null}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setCode('DEMO-2345');
                void submit('DEMO-2345');
              }}
              className="w-full rounded-xl border border-indigo-400/30 bg-indigo-400/10 py-2.5 text-xs text-indigo-200"
            >
              使用演示配对码 DEMO-2345 快速体验
            </button>
          )}

          {error ? (
            <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</p>
          ) : null}

          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="w-full rounded-xl bg-indigo-500 py-3 text-sm font-semibold text-white disabled:opacity-40"
          >
            {busy ? '配对中…' : '开始配对'}
          </button>
        </section>

        <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-4 text-xs text-slate-400">
          <h3 className="text-sm font-medium text-slate-200">配对步骤</h3>
          <ol className="mt-2 list-decimal space-y-1 pl-4">
            <li>PC 端创建一次性 code（KV TTL，一次性使用）。</li>
            <li>手机生成 X25519 + Ed25519 密钥对，发送公钥（pair.begin）。</li>
            <li>PC 确认后手机完成配对（pair.complete），Worker 只保存 token 哈希。</li>
            <li>之后聊天走 Worker LLM 代理；审批/命令走 E2E 密文中继。</li>
          </ol>
          <p className="mt-2 text-[11px] text-slate-500">
            手动创建 code（本机开发）：
            <code className="ml-1 break-all rounded bg-black/30 px-1 py-0.5 font-mono text-[10px] text-indigo-200">
              wrangler dev + POST /v1/pair/code（X-Admin-Token）
            </code>
          </p>
        </section>
      </div>

      {showScanner ? (
        <QrScanner
          onResult={(scanned) => {
            setShowScanner(false);
            const normalized = normalizeCodeInput(scanned);
            setCode(normalized);
            if (isCompleteCode(normalized)) void submit(normalized);
          }}
          onCancel={() => setShowScanner(false)}
        />
      ) : null}
    </div>
  );
}
